import { useCompanyInitialMessage, useUpdateCompanyMessage } from '@/hooks/use-company/use-company';
import { Alert, Flex, Form, Input, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import styles from './initial-message.module.scss';
import { Button } from '../button/button';
import { LoadingOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { CompanySelectBh } from '../company-select-bh/company-select-bh';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';

export function InitialMessage() {
  const [isEditing, setIsEditing] = useState(false);
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_initialMessage',
  });
  const { companyMessage, isLoading } = useCompanyInitialMessage(selectedCompany?.name);
  const [form] = Form.useForm();
  const { updateCompanyMess, isPendingUpdate } = useUpdateCompanyMessage(selectedCompany?.name);

  useEffect(() => {
    if (isEditing && companyMessage) {
      form.setFieldsValue({
        welcome_message: companyMessage.welcome_message || '',
        hello_message: companyMessage.hello_message || '',
      });
    }
  }, [companyMessage, form, isEditing]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const handleSave = (values: { welcome_message?: string; hello_message?: string }) => {
    const company: { welcome_message?: string; hello_message?: string } = {};

    if (
      values.welcome_message !== undefined &&
      values.welcome_message !== companyMessage?.welcome_message
    ) {
      company.welcome_message = values.welcome_message;
    }
    if (
      values.hello_message !== undefined &&
      values.hello_message !== companyMessage?.hello_message
    ) {
      company.hello_message = values.hello_message;
    }

    if (Object.keys(company).length === 0) {
      setIsEditing(false);
      return;
    }

    updateCompanyMess(
      {
        company_name: selectedCompany!.name,
        company,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  return (
    <Flex vertical gap={16}>
      <CompanySelectBh value={selectedCompany} onChange={setSelectedCompany} />
      {isLoading && (
        <div style={{ padding: '10px 0', textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined spin />} />
        </div>
      )}
      {companyMessage && (
        <Flex vertical>
          <Alert
            type="info"
            showIcon
            message={
              <>
                You can use dynamic variables in your messages:
                <Typography.Text code>{'{user_name}'}</Typography.Text> and{' '}
                <Typography.Text code>{'{assistant_name}'}</Typography.Text>. They will be replaced
                with actual values when the message is displayed.
                <p>
                  <strong>example:</strong> Hi{' '}
                  <Typography.Text code>{'{user_name}'}</Typography.Text>. I'm{' '}
                  <Typography.Text code>{'{assistant_name}'}</Typography.Text>, your virtual
                  assistant.
                </p>
              </>
            }
            style={{ marginBottom: 16 }}
          />

          <Form form={form} onFinish={handleSave}>
            <Flex vertical gap={16}>
              <div>
                <p className={styles.welcomeMessTitle}>Welcome message:</p>
                {isEditing ? (
                  <Form.Item name="welcome_message" style={{ marginBottom: 0 }}>
                    <Input.TextArea
                      className={styles.messageTextarea}
                      rows={3}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                    />
                  </Form.Item>
                ) : (
                  <>
                    {companyMessage.welcome_message ? (
                      <span>{companyMessage.welcome_message}</span>
                    ) : (
                      <span className={styles.notCreatedMess}>not created</span>
                    )}
                  </>
                )}
              </div>

              {/* <div>
                <p className={styles.helloMessTitle}>Hello message:</p>
                {isEditing ? (
                  <Form.Item name="hello_message" style={{ marginBottom: 0 }}>
                    <Input.TextArea
                      className={styles.messageTextarea}
                      rows={3}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                    />
                  </Form.Item>
                ) : (
                  <>
                    {companyMessage.hello_message ? (
                      <span>{companyMessage.hello_message || 'Not set'}</span>
                    ) : (
                      <span className={styles.notCreatedMess}>not created</span>
                    )}
                  </>
                )}
              </div> */}

              {isEditing ? (
                <Flex gap={8} className={styles.editSaveButtons}>
                  <Button
                    buttonVariant="secondary"
                    onClick={handleCancel}
                    className={clsx(styles.editButton, styles.cancelButton)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={styles.editButton}
                    buttonVariant="primary"
                    onClick={() => form.submit()}
                    loading={isPendingUpdate}
                  >
                    Save
                  </Button>
                </Flex>
              ) : (
                <Button type="primary" className={styles.editButton} onClick={handleEdit}>
                  <span className={styles.editButtonText}>Edit</span>
                </Button>
              )}
            </Flex>
          </Form>
        </Flex>
      )}
    </Flex>
  );
}
